import React from "react";
import { CheckIcon } from "@heroicons/react/20/solid";

export type VerticalProgressBarStepStatus = "complete" | "current" | "upcoming";

export type VerticalProgressBarStep = {
  name: string;
  description?: React.ReactNode;
  route?: string;
  status: VerticalProgressBarStepStatus;
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function VerticalProgressBarCirclesWithText({
  steps,
}: {
  steps: VerticalProgressBarStep[];
}) {
  return (
    <div className="tw-boundary">
      <nav aria-label="Progress">
        <ol role="list" className="overflow-hidden">
          {steps.map((step, stepIdx) => (
            <li
              key={step.name}
              className={classNames(stepIdx !== steps.length - 1 ? "pb-6" : "", "relative")}
            >
              {step.status === "complete" ? (
                <>
                  {stepIdx !== steps.length - 1 ? (
                    <div
                      aria-hidden="true"
                      className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-primary-600"
                    />
                  ) : null}
                  <div
                    className={`group relative flex ${
                      step.description ? "items-start" : "items-center"
                    }`}
                  >
                    <span className="flex h-9 items-center">
                      <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-primary-600">
                        <CheckIcon aria-hidden="true" className="h-5 w-5 text-white" />
                      </span>
                    </span>
                    <div className="ml-4 flex min-w-0 flex-col">
                      <span className="text-xs font-bold uppercase text-neutral-900">
                        {step.name}
                      </span>
                      <div className="text-sm font-normal text-neutral-600">{step.description}</div>
                    </div>
                  </div>
                </>
              ) : step.status === "current" ? (
                <>
                  {stepIdx !== steps.length - 1 ? (
                    <div
                      aria-hidden="true"
                      className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
                    />
                  ) : null}
                  <div aria-current="step" className="group relative flex items-start">
                    <span aria-hidden="true" className="flex h-9 items-center">
                      <span className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-primary-600 bg-white">
                        <span className="h-2.5 w-2.5 rounded-full bg-primary-600" />
                      </span>
                    </span>
                    <WrappedLink href={step.route}>
                      <div className="ml-4 flex min-w-0 flex-col">
                        <span className="text-xs font-bold uppercase text-primary-600 underline">
                          {step.name}
                        </span>
                        <div className="text-sm font-normal text-neutral-600">
                          {step.description}
                        </div>
                      </div>
                    </WrappedLink>
                  </div>
                </>
              ) : (
                <>
                  {stepIdx !== steps.length - 1 ? (
                    <div
                      aria-hidden="true"
                      className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
                    />
                  ) : null}
                  <div className="group relative flex items-start">
                    <span aria-hidden="true" className="flex h-9 items-center">
                      <span className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white">
                        <span className="h-2.5 w-2.5 rounded-full bg-transparent" />
                      </span>
                    </span>
                    <div className="ml-4 flex min-w-0 flex-col">
                      <span className={`text-xs font-bold uppercase text-neutral-500`}>
                        {step.name}
                      </span>
                      {step.description && (
                        <div className="text-sm font-normal text-neutral-600">
                          {step.description}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
}

// Convenience component to wrap the children in a link if the href is not empty/undefined
const WrappedLink = ({ href, children }: { href?: string; children: React.ReactNode }) => {
  if (!href || href === "") {
    return <>{children}</>;
  }

  return <a href={href}>{children}</a>;
};
